import { takeLatest, put, call } from 'redux-saga/effects';
import { message } from 'antd';
import * as types from '../constants/actionTypes';
import * as actions from '../actions/authActions';
import { authAPI } from '../../api/config';

function* loginSaga(action) {
  try {
    yield put(actions.setLoading(true));
    
    console.log('Making login API call with payload:', action.payload);
    const response = yield call(authAPI.login, action.payload);
    console.log('Login API response:', response.data);
    
    // Store token in localStorage for persistence
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    
    // Dispatch login success - permissions will be stored in Redux by reducer
    yield put(actions.loginSuccess(response.data));
  } catch (error) {
    console.error('Login API error:', error);
    yield put(actions.loginFailure(error.response?.data?.error || 'Login failed'));
  } finally {
    yield put(actions.setLoading(false));
  }
}

function* signupSaga(action) {
  try {
    yield put(actions.setLoading(true));
    
    // Extract only the required fields for API (exclude confirmPassword)
    const { name, email, password } = action.payload;
    const apiPayload = { name, email, password };
    
    console.log('Making signup API call with payload:', apiPayload);
    const response = yield call(authAPI.register, apiPayload);
    console.log('Signup API response:', response.data);
    
    // Store token in localStorage if provided
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    
    yield put(actions.signupSuccess(response.data));
    message.success('Account created successfully! Welcome to Kalapi Prints.');
    
    // Optionally auto-login after successful signup
    if (!response.data.token) {
      yield put(actions.loginRequest({
        email: action.payload.email,
        password: action.payload.password
      }));
    }
  } catch (error) {
    console.error('Signup API error:', error);
    yield put(actions.signupFailure(error.response?.data?.error || 'Registration failed'));
  } finally {
    yield put(actions.setLoading(false));
  }
}

function* logoutSaga() {
  try {
    yield call(authAPI.logout);
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear only token from localStorage - permissions are in Redux only
    localStorage.removeItem('token');
    // No need to dispatch clearPermissions - LOGOUT action already clears Redux permissions
  }
}

// Forgot Password saga
function* forgotPasswordSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const response = yield call(authAPI.forgotPassword, action.payload);
    yield put(actions.forgotPasswordSuccess(response.data));
    message.success(response.data.message || 'Password reset instructions have been sent to your email.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to send reset email';
    yield put(actions.forgotPasswordFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Reset Password saga
function* resetPasswordSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const { token, password } = action.payload;
    const response = yield call(authAPI.resetPassword, token, password);
    yield put(actions.resetPasswordSuccess(response.data));
    message.success('Password reset successfully. You can now log in with your new password.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to reset password';
    yield put(actions.resetPasswordFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Change Password saga
function* changePasswordSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const { currentPassword, newPassword } = action.payload;
    const response = yield call(authAPI.changePassword, currentPassword, newPassword);
    yield put(actions.changePasswordSuccess(response.data));
    message.success('Password changed successfully.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to change password';
    yield put(actions.changePasswordFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Verify Email saga
function* verifyEmailSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const response = yield call(authAPI.verifyEmail, action.payload);
    yield put(actions.verifyEmailSuccess(response.data));
    message.success('Email verified successfully. Your account is now active.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to verify email';
    yield put(actions.verifyEmailFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Resend Verification saga
function* resendVerificationSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const response = yield call(authAPI.resendVerification, action.payload);
    yield put(actions.resendVerificationSuccess(response.data));
    message.success(response.data.message || 'Verification email sent. Please check your inbox.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to resend verification';
    yield put(actions.resendVerificationFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Get Profile saga
function* getProfileSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const response = yield call(authAPI.getProfile);
    yield put(actions.getProfileSuccess(response.data));
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to fetch profile';
    yield put(actions.getProfileFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

// Update Profile saga
function* updateProfileSaga(action) {
  try {
    yield put(actions.setLoading(true));
    const response = yield call(authAPI.updateProfile, action.payload);
    yield put(actions.updateProfileSuccess(response.data));
    message.success('Profile updated successfully.');
  } catch (error) {
    const errorMessage = error.response?.data?.error || 'Failed to update profile';
    yield put(actions.updateProfileFailure(errorMessage));
    message.error(errorMessage);
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchAuth() {
  console.log('Auth saga watcher initialized');
  console.log('SIGNUP_REQUEST action type:', types.SIGNUP_REQUEST);
  
  yield takeLatest(types.LOGIN_REQUEST, loginSaga);
  yield takeLatest(types.SIGNUP_REQUEST, function*(action) {
    console.log('SIGNUP_REQUEST action received in saga:', action);
    yield* signupSaga(action);
  });
  yield takeLatest(types.LOGOUT, logoutSaga);
  yield takeLatest(types.FORGOT_PASSWORD_REQUEST, forgotPasswordSaga);
  yield takeLatest(types.RESET_PASSWORD_REQUEST, resetPasswordSaga);
  yield takeLatest(types.CHANGE_PASSWORD_REQUEST, changePasswordSaga);
  yield takeLatest(types.VERIFY_EMAIL_REQUEST, verifyEmailSaga);
  yield takeLatest(types.RESEND_VERIFICATION_REQUEST, resendVerificationSaga);
  yield takeLatest(types.GET_PROFILE_REQUEST, getProfileSaga);
  yield takeLatest(types.UPDATE_PROFILE_REQUEST, updateProfileSaga);
}